import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import ar from './ar.json';
import en from './en.json';

/**
 * i18next configuration for Nour Al-Arabia
 *
 * Features:
 * - Arabic (ar) as primary/default language
 * - English (en) as secondary language
 * - Language detection: localStorage first, then browser
 * - Persists language selection to localStorage
 * - No suspense mode for immediate rendering
 */

const resources = {
  ar: { translation: ar },
  en: { translation: en },
};

i18n
  // Language detector plugin
  .use(LanguageDetector)
  // React-i18next integration
  .use(initReactI18next)
  // Initialize configuration
  .init({
    resources,
    // Arabic is the fallback and default language
    fallbackLng: 'ar',
    // Supported languages
    supportedLngs: ['ar', 'en'],

    // Language detection configuration
    detection: {
      // Order of detection methods (localStorage first, then browser)
      order: ['localStorage', 'navigator'],
      // Cache user language selection in localStorage
      caches: ['localStorage'],
      // Key for localStorage
      lookupLocalStorage: 'nour-arabia-language',
    },

    // Interpolation settings
    interpolation: {
      // React already protects from XSS
      escapeValue: false,
    },

    // Disable suspense for immediate rendering
    react: {
      useSuspense: false,
    },

    // Debug mode (disabled in production)
    debug: import.meta.env.DEV,

    // Key separator for nested translations (e.g., common.appName)
    keySeparator: '.',
    // Namespace separator
    nsSeparator: ':',

    // Return empty string for missing keys in production
    returnEmptyString: false,
    // Return key for missing translations
    returnNull: false,
  });

/**
 * Get the text direction for a given language
 */
export const getDirectionForLanguage = (lang: string): 'rtl' | 'ltr' => {
  return lang === 'ar' ? 'rtl' : 'ltr';
};

/**
 * Check if the current language is RTL
 */
export const isRTL = (): boolean => {
  return i18n.language === 'ar';
};

/**
 * Available languages in the application
 */
export const availableLanguages = [
  { code: 'ar', name: 'العربية', nameEn: 'Arabic', dir: 'rtl' as const },
  { code: 'en', name: 'English', nameEn: 'English', dir: 'ltr' as const },
] as const;

export type LanguageCode = (typeof availableLanguages)[number]['code'];

export default i18n;
